import type { ColDef, ColGroupDef } from "ag-grid-community";

const isGroup = (def: ColDef | ColGroupDef): def is ColGroupDef =>
  "children" in def;

// field/colId of the static row-label columns (RatesGrid/PlanEditor's benefit
// labels, SummaryGrid's summary type), which must stay a fixed size.
const FIXED_LABEL_COLUMN_IDS = new Set(["benefit.label", "summaryType"]);

/**
 * Returns column defs with every scalable leaf column's width values scaled
 * by `factor` (1 = unchanged). Drives the narrow/medium/wide column-width
 * presets shared with the PlanDesigner grid: with `flex: 1` columns, lowering
 * `minWidth` lets a space-constrained grid pin each column to the scaled width.
 * Only the row-label column keeps its fixed size so the aligned grids stay in
 * sync — other pinned columns (e.g. the current/requested carrier column)
 * scale like any other column.
 */
export const scaleColumnDefWidths = (
  defs: (ColDef | ColGroupDef)[],
  factor: number,
): (ColDef | ColGroupDef)[] => {
  if (factor === 1) return defs;
  return defs.map((def) => {
    if (isGroup(def)) {
      return { ...def, children: scaleColumnDefWidths(def.children, factor) };
    }
    if (
      (def.colId && FIXED_LABEL_COLUMN_IDS.has(def.colId)) ||
      (def.field && FIXED_LABEL_COLUMN_IDS.has(def.field))
    ) {
      return def;
    }
    const scaled: ColDef = { ...def };
    if (typeof def.minWidth === "number") {
      scaled.minWidth = Math.round(def.minWidth * factor);
    }
    if (typeof def.width === "number") {
      scaled.width = Math.round(def.width * factor);
    }
    if (typeof def.maxWidth === "number") {
      scaled.maxWidth = Math.round(def.maxWidth * factor);
    }
    return scaled;
  });
};
